import React, { useState, useEffect, useRef } from 'react';
import {
  View, Text, StyleSheet, ScrollView, TextInput, TouchableOpacity,
  KeyboardAvoidingView, Platform, Switch, ActivityIndicator,
  FlatList,
} from 'react-native';
import Modal from '../components/Modal';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';
import Icon from 'react-native-vector-icons/Ionicons';
import Toast from 'react-native-toast-message';
import { StackScreenProps } from '@react-navigation/stack';
import { updateProfile, submitProfile, getGotras, getNadis, getStates, getDistricts, getEducations, getCommunities, getProfessions } from '../services/api';
import { THEME } from '../components';
import { RELIGIONS, MARITAL_STATUSES, JOB_TYPES, BODY_TYPES, BLOOD_GROUPS, MOTHER_TONGUES, FAMILY_TYPES, FAMILY_STATUS_OPTIONS, FAMILY_VALUES, DIET_OPTIONS, SMOKING_OPTIONS, DRINKING_OPTIONS, INCOME_RANGES, NAKSHATRA_OPTIONS, NAKSHATRA_LABELS, RASHI_OPTIONS, RASHI_LABELS } from '../constants';
import { useLanguage } from '../context/LanguageContext';
import { useAuthContext } from '../context/AuthContext';
import type { RootStackParamList, GotraMasterResponse, NadiMasterResponse, StateResponse, DistrictResponse, EducationMasterResponse, CommunitySurnameResponse, ProfessionMasterResponse } from '../types';

type Props = StackScreenProps<RootStackParamList, 'EditProfile'>;

// ─── Constants ────────────────────────────────────────────────────────────────

const TOTAL_STEPS = 8;


// ─── Sub-components ───────────────────────────────────────────────────────────

const Field: React.FC<{ label: string; required?: boolean; error?: string; children: React.ReactNode }> = ({
  label, required = false, error, children,
}) => (
  <View style={styles.field}>
    <Text style={styles.label}>{label}{required && <Text style={{ color: THEME.primary }}> *</Text>}</Text>
    {children}
    {error ? <Text style={styles.fieldError}>⚠ {error}</Text> : null}
  </View>
);

const TF: React.FC<{
  value: string;
  onChangeText: (v: string) => void;
  placeholder: string;
  keyboardType?: 'default' | 'numeric' | 'email-address' | 'phone-pad';
  multiline?: boolean;
  hasError?: boolean;
  maxLength?: number;
  numberOfLines?: number;
}> = ({ value, onChangeText, placeholder, keyboardType = 'default', multiline = false, hasError = false, maxLength, numberOfLines }) => (
  <View>
    <TextInput
      style={[
        styles.input,
        multiline && { height: numberOfLines ? numberOfLines * 24 : 80, textAlignVertical: 'top' },
        hasError && styles.inputError,
      ]}
      value={value}
      onChangeText={onChangeText}
      placeholder={placeholder}
      placeholderTextColor={THEME.textLight}
      keyboardType={keyboardType}
      multiline={multiline}
      maxLength={maxLength}
      numberOfLines={numberOfLines}
      autoCapitalize={keyboardType === 'email-address' ? 'none' : 'sentences'}
      scrollEnabled={multiline}
    />
    {maxLength && multiline ? (
      <Text style={[styles.charCount, value.length >= maxLength && styles.charCountLimit]}>
        {value.length}/{maxLength}
      </Text>
    ) : null}
  </View>
);

const parseHeight = (v: string): { feet: string; inches: string } => {
  const m = v.match(/^(\d+)'(\d*)$/);
  if (m) return { feet: m[1], inches: m[2] };
  return { feet: '', inches: '' };
};

const HeightInput: React.FC<{ value: string; onChange: (v: string) => void; hasError?: boolean }> = ({
  value, onChange, hasError,
}) => {
  const { feet, inches } = parseHeight(value);
  const update = (f: string, i: string) => {
    if (!f && !i) { onChange(''); return; }
    onChange(`${f}'${i}`);
  };
  return (
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
      <View style={{ flexDirection: 'row', alignItems: 'center', flex: 1, gap: 6 }}>
        <TextInput
          style={[styles.input, { flex: 1, textAlign: 'center' }, hasError && styles.inputError]}
          value={feet}
          onChangeText={v => {
            const n = v.replace(/[^0-9]/g, '').slice(0, 1);
            if (n && parseInt(n, 10) > 9) return;
            update(n, inches);
          }}
          placeholder="0"
          placeholderTextColor={THEME.textLight}
          keyboardType="number-pad"
          maxLength={1}
        />
        <Text style={{ fontSize: 15, color: THEME.textLight, fontWeight: '600' }}>ft</Text>
      </View>
      <View style={{ flexDirection: 'row', alignItems: 'center', flex: 1, gap: 6 }}>
        <TextInput
          style={[styles.input, { flex: 1, textAlign: 'center' }, hasError && styles.inputError]}
          value={inches}
          onChangeText={v => {
            const n = v.replace(/[^0-9]/g, '').slice(0, 2);
            const num = parseInt(n, 10);
            if (n && num > 11) return;
            if (n && parseInt(feet || '0', 10) === 9 && num > 9) return;
            update(feet, n);
          }}
          placeholder="0"
          placeholderTextColor={THEME.textLight}
          keyboardType="number-pad"
          maxLength={2}
        />
        <Text style={{ fontSize: 15, color: THEME.textLight, fontWeight: '600' }}>in</Text>
      </View>
    </View>
  );
};

const ChipSelect: React.FC<{ options: readonly string[]; value: string; onChange: (v: string) => void }> = ({
  options, value, onChange,
}) => (
  <View style={styles.chipRow}>
    {options.map(opt => (
      <TouchableOpacity key={opt} style={[styles.chip, value === opt && styles.chipActive]} onPress={() => onChange(opt)}>
        <Text style={[styles.chipText, value === opt && styles.chipTextActive]}>{opt}</Text>
      </TouchableOpacity>
    ))}
  </View>
);

// ─── Modal Dropdown Picker ────────────────────────────────────────────────────

const DP: React.FC<{
  value: string;
  options: readonly string[];
  placeholder?: string;
  disabled?: boolean;
  loading?: boolean;
  labelMap?: Record<string, string>;
  altMap?: Record<string, string>;
  onSelect: (val: string) => void;
}> = ({ value, options, placeholder, disabled, loading, labelMap, altMap, onSelect }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const getLabel = (v: string) => labelMap?.[v] ?? v;
  const getAlt = (v: string) => altMap?.[v];
  const filtered = options.filter(o =>
    getLabel(o).toLowerCase().includes(search.toLowerCase()) ||
    (getAlt(o) ?? '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <View>
      <TouchableOpacity
        style={[styles.dropdownBtn, !!value && styles.dropdownBtnActive, disabled && styles.dropdownBtnDisabled]}
        onPress={() => { if (!disabled && !loading) { setSearch(''); setOpen(true); } }}
        activeOpacity={0.8}>
        {loading
          ? <ActivityIndicator size="small" color={THEME.primary} style={{ marginRight: 8 }} />
          : null}
        <Text style={[styles.dropdownBtnText, !!value && styles.dropdownBtnTextActive]} numberOfLines={1}>
          {value ? getLabel(value) : (placeholder || 'Select...')}
        </Text>
        <Icon name="chevron-down" size={16} color={value ? THEME.primary : THEME.textLight} />
      </TouchableOpacity>

      <Modal visible={open} animationType="slide" transparent onRequestClose={() => { setOpen(false); setSearch(''); }}>
        <View style={styles.modalOverlay}>
          <SafeAreaView style={styles.modalSheet}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>{placeholder ?? 'Select'}</Text>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
                {!!value && (
                  <TouchableOpacity onPress={() => { onSelect(''); setOpen(false); setSearch(''); }} style={{ paddingVertical: 10, paddingHorizontal: 8 }}>
                    <Text style={{ fontSize: 13, color: THEME.primary, fontWeight: '600' }}>Clear</Text>
                  </TouchableOpacity>
                )}
                <TouchableOpacity onPress={() => { setOpen(false); setSearch(''); }} style={{ padding: 10 }}>
                  <Icon name="close" size={22} color={THEME.text} />
                </TouchableOpacity>
              </View>
            </View>
            <View style={styles.modalSearch}>
              <Icon name="search-outline" size={18} color={THEME.textLight} />
              <TextInput
                style={styles.modalSearchInput}
                value={search}
                onChangeText={setSearch}
                placeholder="Search..."
                placeholderTextColor={THEME.textLight}
                autoFocus
              />
              {search.length > 0 && (
                <TouchableOpacity onPress={() => setSearch('')}>
                  <Icon name="close-circle" size={18} color={THEME.textLight} />
                </TouchableOpacity>
              )}
            </View>
            <FlatList
              data={filtered}
              keyExtractor={item => item}
              renderItem={({ item }) => {
                const alt = getAlt(item);
                return (
                  <TouchableOpacity
                    style={[styles.modalOption, value === item && styles.modalOptionActive]}
                    onPress={() => { onSelect(value === item ? '' : item); setOpen(false); setSearch(''); }}>
                    <View style={{ flex: 1 }}>
                      <Text style={[styles.modalOptionText, value === item && styles.modalOptionTextActive]}>
                        {getLabel(item)}
                      </Text>
                      {alt && alt !== getLabel(item) && (
                        <Text style={[styles.modalOptionAlt, value === item && styles.modalOptionAltActive]}>
                          {alt}
                        </Text>
                      )}
                    </View>
                    {value === item && (
                      <Icon name="checkmark" size={18} color={THEME.primary} />
                    )}
                  </TouchableOpacity>
                );
              }}
              keyboardShouldPersistTaps="handled"
              contentContainerStyle={{ paddingBottom: 40 }}
              ListEmptyComponent={
                <View style={styles.modalEmpty}>
                  <Icon name="search-outline" size={32} color={THEME.border} />
                  <Text style={styles.modalEmptyText}>No results for "{search}"</Text>
                </View>
              }
            />
          </SafeAreaView>
        </View>
      </Modal>
    </View>
  );
};

// ─── Helper ───────────────────────────────────────────────────────────────────

const nonEmpty = (data: Record<string, unknown>): Record<string, unknown> =>
  Object.fromEntries(Object.entries(data).filter(([, v]) => v !== '' && v !== null && v !== undefined));

// ─── Screen ───────────────────────────────────────────────────────────────────

const EditProfileScreen: React.FC<Props> = ({ route, navigation }) => {
  const { t } = useLanguage();
  const { user } = useAuthContext();
  const insets = useSafeAreaInsets();
  const { profile } = route.params;

  const [form, setForm] = useState({
    name: profile.name ?? '',
    gender: profile.gender ?? 'FEMALE',
    email: profile.email ?? '',
    contactNo: profile.contactNo ?? '',
    maritalStatus: profile.maritalStatus ?? '',
    fatherName: profile.fatherName ?? '',
    motherName: profile.motherName ?? '',
    dob: profile.dob ?? '',
    dobTime: profile.dobTime ?? '',
    birthPlace: profile.birthPlace ?? '',
    personHeight: profile.personHeight ?? '',
    bodyType: profile.bodyType ?? '',
    motherTongue: profile.motherTongue ?? '',
    bloodGroup: profile.bloodGroup ?? '',
    otherContactNo: profile.otherContactNo ?? '',
    address: profile.address ?? '',
    district: profile.district ?? '',
    stateName: profile.stateName ?? '',
    pinCode: profile.pinCode ?? '',
    nativePlace: profile.nativePlace ?? '',
    workAddress: profile.workAddress ?? '',
    highestEducation: profile.highestEducation ?? '',
    eduSubCategory: profile.eduSubCategory ?? '',
    instituteName: profile.instituteName ?? '',
    otherDegree: profile.otherDegree ?? '',
    job: profile.job ?? '',
    jobSector: profile.jobSector ?? '',
    jobType: profile.jobType ?? '',
    annualIncome: profile.annualIncome?.toString() ?? '',
    religion: profile.religion ?? '',
    caste: profile.caste ?? '',
    casteSubType1: profile.casteSubType1 ?? '',
    surname: profile.surname ?? '',
    gotra: profile.gotra ?? '',
    nadi: profile.nadi ?? '',
    manglik: profile.manglik ?? false,
    kundliAvailable: profile.kundliAvailable ?? false,
    nakshatra: profile.nakshatra ?? '',
    rashi: profile.rashi ?? '',
    noOfBrothers: profile.noOfBrothers?.toString() ?? '',
    noOfSisters: profile.noOfSisters?.toString() ?? '',
    marriedBrothers: profile.marriedBrothers?.toString() ?? '',
    marriedSisters: profile.marriedSisters?.toString() ?? '',
    fatherAlive: profile.fatherAlive ?? true,
    motherAlive: profile.motherAlive ?? true,
    fatherJobType: profile.fatherJobType ?? '',
    motherJobType: profile.motherJobType ?? '',
    familyType: profile.familyType ?? '',
    familyStatus: profile.familyStatus ?? '',
    familyValues: profile.familyValues ?? '',
    diet: profile.diet ?? '',
    smoking: profile.smoking ?? '',
    drinking: profile.drinking ?? '',
    speciallyAbled: profile.speciallyAbled ?? false,
    specialAbility: profile.specialAbility ?? '',
    profileDetails: profile.profileDetails ?? '',
  });

  const [step, setStep] = useState(1);
  const [completionPct, setCompletionPct] = useState(profile.completionPercentage ?? 0);
  const [submitting, setSubmitting] = useState(false);
  const [nextSaving, setNextSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [gotras, setGotras] = useState<GotraMasterResponse[]>([]);
  const [loadingGotras, setLoadingGotras] = useState(false);
  const [nadis, setNadis] = useState<NadiMasterResponse[]>([]);
  const [loadingNadis, setLoadingNadis] = useState(false);
  const [states, setStates] = useState<StateResponse[]>([]);
  const [loadingStates, setLoadingStates] = useState(false);
  const [districts, setDistricts] = useState<DistrictResponse[]>([]);
  const [loadingDistricts, setLoadingDistricts] = useState(false);
  const [educations, setEducations] = useState<EducationMasterResponse[]>([]);
  const [loadingEducations, setLoadingEducations] = useState(false);
  const [communities, setCommunities] = useState<CommunitySurnameResponse[]>([]);
  const [loadingCommunities, setLoadingCommunities] = useState(false);
  const [professionSectors, setProfessionSectors] = useState<ProfessionMasterResponse[]>([]);
  const [loadingProfessions, setLoadingProfessions] = useState(false);
  // "Other" mode flags — true when value doesn't match any master option
  const [otherEdu, setOtherEdu] = useState(false);
  const [otherEduSub, setOtherEduSub] = useState(false);
  const [otherCaste, setOtherCaste] = useState(false);
  const [otherCasteSub1, setOtherCasteSub1] = useState(false);
  const [otherGotra, setOtherGotra] = useState(false);
  const [otherNadi, setOtherNadi] = useState(false);
  const [otherJob, setOtherJob] = useState(false);
  const [otherJobSector, setOtherJobSector] = useState(false);
  const scrollRef = useRef<ScrollView>(null);

  useEffect(() => {
    setLoadingGotras(true);
    getGotras()
      .then(res => {
        setGotras(res.data);
        if (profile.gotra && !res.data.find((g: GotraMasterResponse) => g.active && g.gotra.en === profile.gotra)) {
          setOtherGotra(true);
        }
      })
      .catch(() => Toast.show({ type: 'error', text1: 'Failed to load gotras' }))
      .finally(() => setLoadingGotras(false));

    setLoadingNadis(true);
    getNadis()
      .then(res => {
        setNadis(res.data);
        if (profile.nadi && !res.data.find((n: NadiMasterResponse) => n.active && n.nadi.en === profile.nadi)) {
          setOtherNadi(true);
        }
      })
      .catch(() => Toast.show({ type: 'error', text1: 'Failed to load nadis' }))
      .finally(() => setLoadingNadis(false));

    setLoadingStates(true);
    getStates()
      .then(res => {
        setStates(res.data);
        if (profile.stateName) {
          const match = res.data.find((s: StateResponse) => s.en === profile.stateName);
          if (match) {
            setLoadingDistricts(true);
            getDistricts(match.stateCode)
              .then(dr => setDistricts(dr.data))
              .catch(() => {})
              .finally(() => setLoadingDistricts(false));
          }
        }
      })
      .catch(() => Toast.show({ type: 'error', text1: 'Failed to load states' }))
      .finally(() => setLoadingStates(false));

    setLoadingEducations(true);
    getEducations()
      .then(res => {
        setEducations(res.data);
        if (profile.highestEducation && !res.data.find((e: EducationMasterResponse) => e.active && e.educationLevel.en === profile.highestEducation)) {
          setOtherEdu(true);
          if (profile.eduSubCategory) setOtherEduSub(true);
        } else if (profile.eduSubCategory) {
          const entry = res.data.find((e: EducationMasterResponse) => e.active && e.educationLevel.en === profile.highestEducation);
          if (!entry || !entry.subCategories.find(s => s.en === profile.eduSubCategory)) {
            setOtherEduSub(true);
          }
        }
      })
      .catch(() => Toast.show({ type: 'error', text1: 'Failed to load educations' }))
      .finally(() => setLoadingEducations(false));

    setLoadingCommunities(true);
    getCommunities()
      .then(res => {
        setCommunities(res.data);
        const active = res.data.filter((c: CommunitySurnameResponse) => c.active);
        const casteMatches = !!active.find(c => c.community.en === profile.caste);
        if (profile.caste && !casteMatches) {
          setOtherCaste(true);
          if (profile.casteSubType1) setOtherCasteSub1(true);
        } else if (profile.caste) {
          const comm = active.find(c => c.community.en === profile.caste);
          const subgroupMatches = !!comm?.subgroups.find(s => s.name.en === profile.casteSubType1);
          if (profile.casteSubType1 && !subgroupMatches) setOtherCasteSub1(true);
        }
      })
      .catch(() => Toast.show({ type: 'error', text1: 'Failed to load communities' }))
      .finally(() => setLoadingCommunities(false));

    setLoadingProfessions(true);
    getProfessions()
      .then(res => {
        setProfessionSectors(res.data);
        const allProfessions = res.data.flatMap((s: ProfessionMasterResponse) => s.professions.map(p => p.en));
        if (profile.job && !allProfessions.includes(profile.job)) setOtherJob(true);
        if (profile.jobSector) {
          const allSectors = res.data.map((s: ProfessionMasterResponse) => s.sector.en);
          if (!allSectors.includes(profile.jobSector)) setOtherJobSector(true);
        }
      })
      .catch(() => Toast.show({ type: 'error', text1: 'Failed to load professions' }))
      .finally(() => setLoadingProfessions(false));
  }, []);

  const onStateSelected = (stateName: string) => {
    setForm(f => ({ ...f, stateName, district: '' }));
    setDistricts([]);
    const state = states.find(s => s.en === stateName);
    if (!state) return;
    setLoadingDistricts(true);
    getDistricts(state.stateCode)
      .then(res => setDistricts(res.data))
      .catch(() => Toast.show({ type: 'error', text1: 'Failed to load districts' }))
      .finally(() => setLoadingDistricts(false));
  };

  const set = <K extends keyof typeof form>(key: K, val: (typeof form)[K]) => {
    setForm(f => ({ ...f, [key]: val }));
    if (errors[key as string]) setErrors(e => { const n = { ...e }; delete n[key as string]; return n; });
  };

  const getStepPatch = (s: number): Record<string, unknown> => {
    switch (s) {
      case 1: return { name: form.name, email: user?.email ?? form.email, contactNo: form.contactNo, maritalStatus: form.maritalStatus, dob: form.dob };
      case 2: return {
        fatherName: form.fatherName, motherName: form.motherName,
        dobTime: form.dobTime, birthPlace: form.birthPlace, personHeight: form.personHeight,
        bodyType: form.bodyType, motherTongue: form.motherTongue,
        bloodGroup: form.bloodGroup,
      };
      case 3: return { address: form.address, district: form.district, stateName: form.stateName, pinCode: form.pinCode, nativePlace: form.nativePlace, workAddress: form.workAddress };
      case 4: return { highestEducation: form.highestEducation, eduSubCategory: form.eduSubCategory, instituteName: form.instituteName || undefined, otherDegree: form.otherDegree || undefined };
      case 5: return { job: form.job, jobSector: form.jobSector, jobType: form.jobType, annualIncome: form.annualIncome ? parseInt(form.annualIncome, 10) : undefined };
      case 6: return { religion: form.religion, caste: form.caste, casteSubType1: form.casteSubType1, surname: form.surname, gotra: form.gotra, nadi: form.nadi, manglik: form.manglik, kundliAvailable: form.kundliAvailable, nakshatra: form.nakshatra || undefined, rashi: form.rashi || undefined };
      case 7: return {
        noOfBrothers: form.noOfBrothers ? parseInt(form.noOfBrothers, 10) : undefined,
        noOfSisters: form.noOfSisters ? parseInt(form.noOfSisters, 10) : undefined,
        marriedBrothers: form.marriedBrothers ? parseInt(form.marriedBrothers, 10) : undefined,
        marriedSisters: form.marriedSisters ? parseInt(form.marriedSisters, 10) : undefined,
        fatherAlive: form.fatherAlive, motherAlive: form.motherAlive,
        fatherJobType: form.fatherJobType, motherJobType: form.motherJobType,
        familyType: form.familyType, familyStatus: form.familyStatus, familyValues: form.familyValues,
      };
      case 8: return {
        diet: form.diet, smoking: form.smoking, drinking: form.drinking,
        speciallyAbled: form.speciallyAbled, specialAbility: form.speciallyAbled ? form.specialAbility : '',
        otherContactNo: form.otherContactNo, profileDetails: form.profileDetails,
      };
      default: return {};
    }
  };

  const patchDraft = async (s: number): Promise<void> => {
    const raw = getStepPatch(s);
    const patch = nonEmpty(raw);
    // Optional free-text fields: nonEmpty strips '' so re-include them when user has a value or profile had one.
    // This lets users clear a previously-entered value by leaving the field blank.
    const clearable: Array<[number, string]> = [
      [2, 'motherName'], [2, 'dobTime'], [2, 'birthPlace'], [2, 'personHeight'],
      [3, 'address'], [3, 'pinCode'], [3, 'nativePlace'], [3, 'workAddress'],
      [4, 'eduSubCategory'], [4, 'instituteName'], [4, 'otherDegree'],
      [5, 'job'],
      [6, 'casteSubType1'], [6, 'surname'], [6, 'gotra'], [6, 'nadi'], [6, 'nakshatra'], [6, 'rashi'],
      [7, 'fatherJobType'], [7, 'motherJobType'], [7, 'familyType'], [7, 'familyStatus'], [7, 'familyValues'],
      [8, 'otherContactNo'], [8, 'profileDetails'],
    ];
    for (const [step, key] of clearable) {
      const fv = form[key as keyof typeof form] as string | undefined;
      const pv = profile[key as keyof typeof profile] as string | undefined;
      if (s === step && (fv || pv)) patch[key] = fv ?? '';
    }
    if (Object.keys(patch).length === 0) return;
    const res = await updateProfile(profile.id, patch);
    setCompletionPct(res.data.completionPercentage ?? completionPct);
  };

  const validateHeight = (v: string): string | undefined => {
    if (!v.trim()) return undefined;
    const m = v.match(/^(\d+)'(\d*)$/);
    if (!m) return undefined;
    const feet = parseInt(m[1], 10);
    const inch = m[2] ? parseInt(m[2], 10) : 0;
    if (feet === 0) return 'Please enter a valid height';
    if (feet > 9 || (feet === 9 && inch > 9)) return "Height must be 9'9\" or less";
    return undefined;
  };

  const validateStep1 = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!form.name.trim()) newErrors.name = 'Name is required';
    else if (form.name.trim().length > 100) newErrors.name = 'Name must be 100 characters or less';
    if (!form.maritalStatus) newErrors.maritalStatus = 'Marital status is required';
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) newErrors.email = 'Enter a valid email address';
    if (!/^[0-9]{10}$/.test(form.contactNo)) newErrors.contactNo = 'Must be exactly 10 digits';
    if (!form.dob.trim()) {
      newErrors.dob = 'Date of birth is required';
    } else if (!/^\d{2}\/\d{2}\/\d{4}$/.test(form.dob)) {
      newErrors.dob = 'Enter date as DD/MM/YYYY';
    } else {
      const [dd, mm, yyyy] = form.dob.split('/').map(Number);
      const parsed = new Date(yyyy, mm - 1, dd);
      if (parsed.getDate() !== dd || parsed.getMonth() !== mm - 1 || parsed.getFullYear() !== yyyy) {
        newErrors.dob = 'Enter a valid date';
      } else {
        const age = Math.floor((Date.now() - parsed.getTime()) / (365.25 * 24 * 3600 * 1000));
        if (age < 18) newErrors.dob = 'Must be at least 18 years old';
        else if (age > 60) newErrors.dob = 'Please enter a valid date of birth';
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateAll = (): { valid: boolean; firstErrorStep: number } => {
    const e: Record<string, string> = {};
    if (!form.name.trim()) e.name = 'Name is required';
    else if (form.name.trim().length > 100) e.name = 'Name must be 100 characters or less';
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) e.email = 'Enter a valid email address';
    if (!/^[0-9]{10}$/.test(form.contactNo)) e.contactNo = 'Must be exactly 10 digits';
    if (form.fatherName.trim().length > 100) e.fatherName = "Father's name must be 100 characters or less";
    if (form.motherName.trim().length > 100) e.motherName = "Mother's name must be 100 characters or less";
    if (!form.dob.trim()) {
      e.dob = 'Date of birth is required';
    } else if (!/^\d{2}\/\d{2}\/\d{4}$/.test(form.dob)) {
      e.dob = 'Enter date as DD/MM/YYYY';
    } else {
      const [dd, mm, yyyy] = form.dob.split('/').map(Number);
      const parsed = new Date(yyyy, mm - 1, dd);
      if (parsed.getDate() !== dd || parsed.getMonth() !== mm - 1 || parsed.getFullYear() !== yyyy) {
        e.dob = 'Enter a valid date';
      } else {
        const age = Math.floor((Date.now() - parsed.getTime()) / (365.25 * 24 * 3600 * 1000));
        if (age < 18) e.dob = 'Must be at least 18 years old';
        else if (age > 60) e.dob = 'Please enter a valid date of birth';
      }
    }
    const heightErr = validateHeight(form.personHeight);
    if (heightErr) e.personHeight = heightErr;
    if (form.address.trim().length > 100) e.address = 'Address must be 100 characters or less';
    if (form.workAddress.trim().length > 100) e.workAddress = 'Work address must be 100 characters or less';
    if (!form.stateName) e.stateName = 'State is required';
    if (!form.district) e.district = 'District is required';
    if (form.job.trim().length > 50) e.job = 'Job title must be 50 characters or less';
    if (form.profileDetails.trim().length > 1000) e.profileDetails = 'Profile details must be 1000 characters or less';
    if (!form.religion.trim()) e.religion = 'Religion is required';
    setErrors(e);
    if (Object.keys(e).length === 0) return { valid: true, firstErrorStep: 8 };
    const fieldStep: Record<string, number> = {
      name: 1, email: 1, contactNo: 1, dob: 1, maritalStatus: 1,
      fatherName: 2, motherName: 2, personHeight: 2,
      stateName: 3, district: 3,
      job: 5,
      religion: 6,
      profileDetails: 8,
    };
    const first = Object.keys(e).map(f => fieldStep[f] ?? 6).sort((a, b) => a - b)[0];
    return { valid: false, firstErrorStep: first };
  };

  const goNext = async () => {
    if (step === 1 && !validateStep1()) {
      scrollRef.current?.scrollTo({ y: 0, animated: true });
      return;
    }
    setNextSaving(true);
    try {
      await patchDraft(step);
      setStep(s => s + 1);
      scrollRef.current?.scrollTo({ y: 0, animated: false });
    } catch (e: any) {
      Toast.show({ type: 'error', text1: 'Could not save', text2: e?.response?.data?.message ?? e?.message ?? 'Please check your inputs' });
    } finally {
      setNextSaving(false);
    }
  };

  const goBack = () => {
    setStep(s => s - 1);
    scrollRef.current?.scrollTo({ y: 0, animated: false });
  };

  const handleSubmit = async () => {
    try {
      await patchDraft(8);
    } catch (e: any) {
      Toast.show({ type: 'error', text1: 'Could not save', text2: e?.response?.data?.message ?? e?.message ?? 'Please check your inputs' });
      return;
    }
    const { valid, firstErrorStep } = validateAll();
    if (!valid) {
      setStep(firstErrorStep);
      scrollRef.current?.scrollTo({ y: 0, animated: false });
      Toast.show({ type: 'error', text1: 'Required fields missing', text2: 'Please fill all highlighted fields' });
      return;
    }
    setSubmitting(true);
    try {
      await submitProfile(profile.id);
      Toast.show({ type: 'success', text1: 'Submitted for review!', text2: 'Pending admin approval' });
      navigation.goBack();
    } catch (e) {
      Toast.show({ type: 'error', text1: 'Submission failed', text2: (e as Error).message });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSave = async () => {
    try {
      await patchDraft(step);
    } catch (e: any) {
      Toast.show({ type: 'error', text1: 'Could not save', text2: e?.response?.data?.message ?? e?.message ?? 'Please check your inputs' });
      return;
    }
    Toast.show({ type: 'success', text1: 'Changes saved' });
    navigation.goBack();
  };

  const stepTitles = [
    'Basic Info',
    t('section.personal'),
    t('section.address'),
    t('section.education'),
    t('section.career'),
    t('section.casteFamily'),
    'Family Background',
    'Lifestyle & About',
  ];

  // Derived options for dropdowns — primary label = current lang, alt label = other lang (always shown in list)
  // labelMap combines both scripts: "Brahmin ब्राह्मण"
  const BL = (o: { en: string; hi: string }) => o.hi ? `${o.en}  ${o.hi}` : o.en;
  const activeCommunities = communities.filter(c => c.active &&
    (!form.religion || c.religion === form.religion));
  const casteOptions = [...activeCommunities.map(c => c.community.en), 'Other'];
  const casteLabels: Record<string, string> = {};
  activeCommunities.forEach(c => { casteLabels[c.community.en] = BL(c.community); });
  const selectedComm = activeCommunities.find(c => c.community.en === form.caste);
  const casteSubOptions = [...(selectedComm?.subgroups.map(s => s.name.en) ?? []), 'Other'];
  const casteSubLabels: Record<string, string> = {};
  selectedComm?.subgroups.forEach(s => { casteSubLabels[s.name.en] = BL(s.name); });
  const eduSubOptions = [...(educations.find(e => e.educationLevel.en === form.highestEducation)?.subCategories.map(s => s.en) ?? []), 'Other'];
  const eduLevelLabels: Record<string, string> = {};
  educations.forEach(e => { eduLevelLabels[e.educationLevel.en] = BL(e.educationLevel); });
  const eduSubLabels: Record<string, string> = {};
  educations.find(e => e.educationLevel.en === form.highestEducation)?.subCategories.forEach(s => { eduSubLabels[s.en] = BL(s); });
  const stateLabels: Record<string, string> = {};
  states.forEach(s => { stateLabels[s.en] = BL(s); });
  const districtLabels: Record<string, string> = {};
  districts.forEach(d => { districtLabels[d.en] = BL(d); });
  const gotraLabels: Record<string, string> = {};
  gotras.filter(g => g.active).forEach(g => { gotraLabels[g.gotra.en] = BL(g.gotra); });
  const nadiLabels: Record<string, string> = {};
  nadis.filter(n => n.active).forEach(n => { nadiLabels[n.nadi.en] = BL(n.nadi); });
  const jobSectorOptions = [...professionSectors.map(s => s.sector.en), 'Other'];
  const jobSectorLabelMap: Record<string, string> = {};
  professionSectors.forEach(s => { jobSectorLabelMap[s.sector.en] = BL(s.sector); });
  const selectedSector = professionSectors.find(s => s.sector.en === form.jobSector);
  const sectorProfessions = selectedSector ? selectedSector.professions : professionSectors.flatMap(s => s.professions);
  const professionOptions = [...sectorProfessions.map(p => p.en), 'Other'];
  const professionLabelMap: Record<string, string> = {};
  sectorProfessions.forEach(p => { professionLabelMap[p.en] = BL(p); });

  return (
    <SafeAreaView style={{ flex: 1 }} edges={['bottom']}>
    <KeyboardAvoidingView
      style={{ flex: 1 }}
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      keyboardVerticalOffset={Platform.OS === 'ios' ? 0 : 24}>

      {/* Progress Header */}
      <View style={styles.progressHeader}>
        <View style={styles.progressTopRow}>
          <Text style={styles.stepTitle}>{stepTitles[step - 1]}</Text>
          <Text style={styles.stepCount}>{step} / {TOTAL_STEPS}</Text>
        </View>
        <View style={styles.completionRow}>
          <Text style={styles.completionLabel}>{completionPct}% complete</Text>
        </View>
        <View style={styles.progressBar}>
          <View style={[styles.progressFill, { width: `${(step / TOTAL_STEPS) * 100}%` }]} />
        </View>
      </View>

      <ScrollView
        ref={scrollRef}
        style={styles.container}
        contentContainerStyle={{ paddingBottom: 120 }}
        keyboardShouldPersistTaps="handled"
        keyboardDismissMode="interactive"
        showsVerticalScrollIndicator={false}>

        {/* Step 1: Basic */}
        {step === 1 && (
          <View>
            <Field label={t('field.name')} required error={errors.name}>
              <TF value={form.name} onChangeText={v => set('name', v)} placeholder="Enter full name" hasError={!!errors.name} maxLength={100} />
            </Field>
            <Field label={t('field.gender')} required>
              <View style={styles.readonlyRow}>
                <Text style={styles.readonlyText}>{form.gender === 'MALE' ? 'Male' : 'Female'}</Text>
                <Icon name="lock-closed-outline" size={16} color={THEME.textLight} />
              </View>
              <Text style={styles.genderHint}>
                {'Gender cannot be changed. If you selected it by mistake, please contact JeevanSanyog support — see the '}
                <Text style={styles.genderHintLink} onPress={() => navigation.navigate('Help', {})}>Help</Text>
                {' section.'}
              </Text>
            </Field>
            <Field label={t('field.maritalStatus')} required error={errors.maritalStatus}>
              <ChipSelect options={MARITAL_STATUSES} value={form.maritalStatus} onChange={v => set('maritalStatus', v)} />
            </Field>
            <Field label={t('field.contactNo')} required error={errors.contactNo}>
              <TF value={form.contactNo} onChangeText={v => set('contactNo', v)} placeholder="10-digit mobile number" keyboardType="phone-pad" hasError={!!errors.contactNo} />
            </Field>
            <Field label={`${t('field.dob')} (DD/MM/YYYY)`} required error={errors.dob}>
              <TF
                value={form.dob}
                onChangeText={v => {
                  const digits = v.replace(/\D/g, '').slice(0, 8);
                  let formatted = digits;
                  if (digits.length > 4) formatted = `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
                  else if (digits.length > 2) formatted = `${digits.slice(0, 2)}/${digits.slice(2)}`;
                  set('dob', formatted);
                }}
                placeholder="DD/MM/YYYY"
                keyboardType="numeric"
                hasError={!!errors.dob}
              />
            </Field>
          </View>
        )}

        {/* Step 2: Personal */}
        {step === 2 && (
          <View>
            <Field label={t('field.fatherName')} error={errors.fatherName}>
              <TF value={form.fatherName} onChangeText={v => set('fatherName', v)} placeholder="Father's name" hasError={!!errors.fatherName} maxLength={100} />
            </Field>
            <Field label={t('field.motherName')}>
              <TF value={form.motherName} onChangeText={v => set('motherName', v)} placeholder="Mother's name" maxLength={100} />
            </Field>
            <Field label={`${t('field.birthTime')} (HH:MM, 24-hr)`}>
              <TF value={form.dobTime} onChangeText={v => set('dobTime', v)} placeholder="e.g. 14:30" />
            </Field>
            <Field label={t('field.birthPlace')}>
              <TF value={form.birthPlace} onChangeText={v => set('birthPlace', v)} placeholder="City/Village of birth" maxLength={100} />
            </Field>
            <Field label={t('field.personHeight')} error={errors.personHeight}>
              <HeightInput value={form.personHeight} onChange={v => { set('personHeight', v); setErrors(e => ({ ...e, personHeight: validateHeight(v) ?? '' })); }} hasError={!!errors.personHeight} />
            </Field>
            <Field label={t('field.bodyType')}>
              <ChipSelect options={BODY_TYPES} value={form.bodyType} onChange={v => set('bodyType', v)} />
            </Field>
            <Field label={t('field.motherTongue')}>
              <ChipSelect options={MOTHER_TONGUES} value={form.motherTongue} onChange={v => set('motherTongue', v)} />
            </Field>
            <Field label={t('field.bloodGroup')}>
              <ChipSelect options={BLOOD_GROUPS} value={form.bloodGroup} onChange={v => set('bloodGroup', v)} />
            </Field>
          </View>
        )}

        {/* Step 3: Address */}
        {step === 3 && (
          <View>
            <Field label={t('field.address')} error={errors.address}>
              <TF value={form.address} onChangeText={v => set('address', v)} placeholder="House/Street/Area (optional)" multiline hasError={!!errors.address} maxLength={100} />
            </Field>
            <Field label={t('field.stateName')} required error={errors.stateName}>
              <DP
                value={form.stateName}
                options={states.map(s => s.en)}
                placeholder="Select State"
                loading={loadingStates}
                labelMap={stateLabels}
                onSelect={onStateSelected}
              />
            </Field>
            <Field label={t('field.district')} required error={errors.district}>
              <DP
                value={form.district}
                options={districts.map(d => d.en)}
                placeholder={districts.length === 0 ? 'Select state first' : 'Select District'}
                disabled={!form.stateName}
                loading={loadingDistricts}
                labelMap={districtLabels}
                onSelect={v => set('district', v)}
              />
            </Field>
            <Field label={t('field.pinCode')}>
              <TF value={form.pinCode} onChangeText={v => set('pinCode', v)} placeholder="6-digit pin code" keyboardType="numeric" />
            </Field>
            <Field label={t('field.nativePlace')}>
              <TF value={form.nativePlace} onChangeText={v => set('nativePlace', v)} placeholder="Ancestral village/city (optional)" maxLength={100} />
            </Field>
            <Field label={t('field.workAddress')}>
              <TF value={form.workAddress} onChangeText={v => set('workAddress', v)} placeholder="Work address (optional)" multiline maxLength={100} />
            </Field>
          </View>
        )}

        {/* Step 4: Education */}
        {step === 4 && (
          <View>
            <Field label={t('field.highestEducation')} error={errors.highestEducation}>
              <DP
                value={otherEdu ? 'Other' : form.highestEducation}
                options={[...educations.filter(e => e.active).map(e => e.educationLevel.en), 'Other']}
                placeholder="Select Education"
                loading={loadingEducations}
                labelMap={eduLevelLabels}
                onSelect={v => {
                  if (v === 'Other') { setOtherEdu(true); set('highestEducation', ''); setOtherEduSub(false); set('eduSubCategory', ''); }
                  else { setOtherEdu(false); set('highestEducation', v); setOtherEduSub(false); set('eduSubCategory', ''); }
                }}
              />
              {otherEdu && (
                <View style={styles.otherInput}>
                  <TF value={form.highestEducation} onChangeText={v => set('highestEducation', v)} placeholder="Type your education level..." />
                </View>
              )}
            </Field>
            <Field label={t('field.eduSubCategory')}>
              {otherEdu ? (
                <TF value={form.eduSubCategory} onChangeText={v => set('eduSubCategory', v)} placeholder="Type sub-category (optional)" />
              ) : (
                <>
                  <DP
                    value={otherEduSub ? 'Other' : form.eduSubCategory}
                    options={eduSubOptions}
                    placeholder={!form.highestEducation ? 'Select education first' : 'Select Sub-Category'}
                    disabled={!form.highestEducation}
                    labelMap={eduSubLabels}
                    onSelect={v => {
                      if (v === 'Other') { setOtherEduSub(true); set('eduSubCategory', ''); }
                      else { setOtherEduSub(false); set('eduSubCategory', v); }
                    }}
                  />
                  {otherEduSub && (
                    <View style={styles.otherInput}>
                      <TF value={form.eduSubCategory} onChangeText={v => set('eduSubCategory', v)} placeholder="Type your sub-category..." />
                    </View>
                  )}
                </>
              )}
            </Field>
            <Field label={t('field.instituteName', 'Institute / College')}>
              <TF
                value={form.instituteName}
                onChangeText={v => set('instituteName', v)}
                placeholder="e.g. IIT Bombay, Delhi University"
                maxLength={100}
              />
            </Field>
            <Field label={t('field.otherDegree', 'Any Other Degree')}>
              <TF
                value={form.otherDegree}
                onChangeText={v => set('otherDegree', v)}
                placeholder="e.g. MBA, PhD, CA (optional)"
                maxLength={100}
              />
            </Field>
          </View>
        )}

        {/* Step 5: Career */}
        {step === 5 && (
          <View>
            <Field label={t('field.jobType')}>
              <ChipSelect options={JOB_TYPES} value={form.jobType} onChange={v => set('jobType', v)} />
            </Field>
            <Field label={t('field.jobSector')}>
              <DP
                value={otherJobSector ? 'Other' : form.jobSector}
                options={jobSectorOptions}
                placeholder="Select Sector"
                loading={loadingProfessions}
                labelMap={jobSectorLabelMap}
                onSelect={v => {
                  if (v === 'Other') { setOtherJobSector(true); set('jobSector', ''); setOtherJob(false); set('job', ''); }
                  else { setOtherJobSector(false); set('jobSector', v); setOtherJob(false); set('job', ''); }
                }}
              />
              {otherJobSector && (
                <View style={styles.otherInput}>
                  <TF value={form.jobSector} onChangeText={v => set('jobSector', v)} placeholder="Specify job sector..." maxLength={50} />
                </View>
              )}
            </Field>
            <Field label={t('field.job')}>
              {otherJobSector ? (
                <TF value={form.job} onChangeText={v => set('job', v)} placeholder="Specify profession..." maxLength={50} />
              ) : (
                <>
                  <DP
                    value={otherJob ? 'Other' : form.job}
                    options={professionOptions}
                    placeholder={!form.jobSector ? 'Select sector first' : 'Select Profession'}
                    disabled={!form.jobSector}
                    loading={loadingProfessions}
                    labelMap={professionLabelMap}
                    onSelect={v => {
                      if (v === 'Other') { setOtherJob(true); set('job', ''); }
                      else { setOtherJob(false); set('job', v); }
                    }}
                  />
                  {otherJob && (
                    <View style={styles.otherInput}>
                      <TF value={form.job} onChangeText={v => set('job', v)} placeholder="Specify profession..." maxLength={50} />
                    </View>
                  )}
                </>
              )}
            </Field>
            <Field label={t('field.annualIncome')}>
              <View style={styles.chipRow}>
                {INCOME_RANGES.map(r => {
                  const active = form.annualIncome === r.value.toString();
                  return (
                    <TouchableOpacity key={r.label} style={[styles.chip, active && styles.chipActive]}
                      onPress={() => set('annualIncome', active ? '' : r.value.toString())}>
                      <Text style={[styles.chipText, active && styles.chipTextActive]}>{r.label}</Text>
                    </TouchableOpacity>
                  );
                })}
              </View>
            </Field>
          </View>
        )}

        {/* Step 6: Caste & Family */}
        {step === 6 && (
          <View>
            <Field label={t('field.religion')} required error={errors.religion}>
              <ChipSelect options={RELIGIONS} value={form.religion} onChange={v => {
                set('religion', v);
                set('caste', ''); setOtherCaste(false);
                set('casteSubType1', ''); setOtherCasteSub1(false);
              }} />
            </Field>
            <Field label={t('field.caste')} error={errors.caste}>
              <DP
                value={otherCaste ? 'Other' : form.caste}
                options={casteOptions}
                placeholder="Select Caste/Community"
                loading={loadingCommunities}
                labelMap={casteLabels}
                onSelect={v => {
                  if (v === 'Other') { setOtherCaste(true); set('caste', ''); setOtherCasteSub1(false); set('casteSubType1', ''); }
                  else { setOtherCaste(false); set('caste', v); setOtherCasteSub1(false); set('casteSubType1', ''); }
                }}
              />
              {otherCaste && (
                <View style={styles.otherInput}>
                  <TF value={form.caste} onChangeText={v => set('caste', v)} placeholder="Type your caste/community..." />
                </View>
              )}
            </Field>
            <Field label={t('field.casteSubType1')}>
              {otherCaste ? (
                <TF value={form.casteSubType1} onChangeText={v => set('casteSubType1', v)} placeholder="Type sub-group (optional)" />
              ) : (
                <>
                  <DP
                    value={otherCasteSub1 ? 'Other' : form.casteSubType1}
                    options={casteSubOptions}
                    placeholder={!form.caste ? 'Select caste first' : 'Select Sub-Group'}
                    disabled={!form.caste}
                    labelMap={casteSubLabels}
                    onSelect={v => {
                      if (v === 'Other') { setOtherCasteSub1(true); set('casteSubType1', ''); }
                      else { setOtherCasteSub1(false); set('casteSubType1', v); }
                    }}
                  />
                  {otherCasteSub1 && (
                    <View style={styles.otherInput}>
                      <TF value={form.casteSubType1} onChangeText={v => set('casteSubType1', v)} placeholder="Type your sub-group..." />
                    </View>
                  )}
                </>
              )}
            </Field>
            <Field label={t('field.gotra')} error={errors.gotra}>
              <DP
                value={otherGotra ? 'Other' : form.gotra}
                options={[...gotras.filter(g => g.active).map(g => g.gotra.en), 'Other']}
                placeholder="Select Gotra"
                loading={loadingGotras}
                labelMap={gotraLabels}
                onSelect={v => {
                  if (v === 'Other') { setOtherGotra(true); set('gotra', ''); }
                  else { setOtherGotra(false); set('gotra', v); }
                }}
              />
              {otherGotra && (
                <View style={styles.otherInput}>
                  <TF value={form.gotra} onChangeText={v => set('gotra', v)} placeholder="Type your gotra..." />
                </View>
              )}
            </Field>
            <Field label={t('field.nadi')}>
              <DP
                value={otherNadi ? 'Other' : form.nadi}
                options={[...nadis.filter(n => n.active).map(n => n.nadi.en), 'Other']}
                placeholder="Select Nadi"
                loading={loadingNadis}
                labelMap={nadiLabels}
                onSelect={v => {
                  if (v === 'Other') { setOtherNadi(true); set('nadi', ''); }
                  else { setOtherNadi(false); set('nadi', v); }
                }}
              />
              {otherNadi && (
                <View style={styles.otherInput}>
                  <TF value={form.nadi} onChangeText={v => set('nadi', v)} placeholder="Type your nadi..." />
                </View>
              )}
            </Field>
            <Field label={t('field.manglik')}>
              <View style={styles.switchRow}>
                <Switch value={form.manglik} onValueChange={v => set('manglik', v)}
                  trackColor={{ false: THEME.border, true: THEME.primary }} thumbColor="#fff" />
                <Text style={styles.switchLabel}>{form.manglik ? t('yes') : t('no')}</Text>
              </View>
            </Field>
            <Field label={t('field.kundliAvailable')}>
              <View style={styles.switchRow}>
                <Switch value={form.kundliAvailable} onValueChange={v => set('kundliAvailable', v)}
                  trackColor={{ false: THEME.border, true: THEME.primary }} thumbColor="#fff" />
                <Text style={styles.switchLabel}>{form.kundliAvailable ? t('yes') : t('no')}</Text>
              </View>
            </Field>
            <Field label={t('field.nakshatra', 'Nakshatra (Birth Star)')}>
              <DP
                value={form.nakshatra}
                options={NAKSHATRA_OPTIONS as unknown as string[]}
                placeholder="Select Nakshatra"
                labelMap={NAKSHATRA_LABELS}
                onSelect={v => set('nakshatra', v)}
              />
            </Field>
            <Field label={t('field.rashi', 'Rashi (Moon Sign)')}>
              <DP
                value={form.rashi}
                options={RASHI_OPTIONS as unknown as string[]}
                placeholder="Select Rashi"
                labelMap={RASHI_LABELS}
                onSelect={v => set('rashi', v)}
              />
            </Field>
          </View>
        )}

        {/* Step 7: Family Background */}
        {step === 7 && (
          <View>
            <Field label={t('field.noOfBrothers')}>
              <TF value={form.noOfBrothers} onChangeText={v => set('noOfBrothers', v)} placeholder="e.g. 1" keyboardType="numeric" />
            </Field>
            <Field label={t('field.noOfSisters')}>
              <TF value={form.noOfSisters} onChangeText={v => set('noOfSisters', v)} placeholder="e.g. 2" keyboardType="numeric" />
            </Field>
            <Field label={t('field.marriedBrothers', 'Married Brothers')}>
              <TF value={form.marriedBrothers} onChangeText={v => set('marriedBrothers', v)} placeholder="e.g. 1" keyboardType="numeric" />
            </Field>
            <Field label={t('field.marriedSisters', 'Married Sisters')}>
              <TF value={form.marriedSisters} onChangeText={v => set('marriedSisters', v)} placeholder="e.g. 1" keyboardType="numeric" />
            </Field>
            <Field label={t('field.fatherAlive')}>
              <View style={styles.switchRow}>
                <Switch value={form.fatherAlive} onValueChange={v => set('fatherAlive', v)}
                  trackColor={{ false: THEME.border, true: THEME.primary }} thumbColor="#fff" />
                <Text style={styles.switchLabel}>{form.fatherAlive ? t('yes') : t('no')}</Text>
              </View>
            </Field>
            <Field label={t('field.motherAlive')}>
              <View style={styles.switchRow}>
                <Switch value={form.motherAlive} onValueChange={v => set('motherAlive', v)}
                  trackColor={{ false: THEME.border, true: THEME.primary }} thumbColor="#fff" />
                <Text style={styles.switchLabel}>{form.motherAlive ? t('yes') : t('no')}</Text>
              </View>
            </Field>
            <Field label={t('field.fatherJobType')}>
              <ChipSelect options={JOB_TYPES} value={form.fatherJobType} onChange={v => set('fatherJobType', v)} />
            </Field>
            <Field label={t('field.motherJobType')}>
              <ChipSelect options={JOB_TYPES} value={form.motherJobType} onChange={v => set('motherJobType', v)} />
            </Field>
            <Field label={t('field.familyType')}>
              <ChipSelect options={FAMILY_TYPES} value={form.familyType} onChange={v => set('familyType', v)} />
            </Field>
            <Field label={t('field.familyStatus')}>
              <ChipSelect options={FAMILY_STATUS_OPTIONS} value={form.familyStatus} onChange={v => set('familyStatus', v)} />
            </Field>
            <Field label={t('field.familyValues')}>
              <ChipSelect options={FAMILY_VALUES} value={form.familyValues} onChange={v => set('familyValues', v)} />
            </Field>
          </View>
        )}

        {/* Step 8: Lifestyle & About */}
        {step === 8 && (
          <View>
            <Field label={t('field.diet')} error={errors.diet}>
              <ChipSelect options={DIET_OPTIONS} value={form.diet} onChange={v => set('diet', v)} />
            </Field>
            <Field label={t('field.smoking')} error={errors.smoking}>
              <ChipSelect options={SMOKING_OPTIONS} value={form.smoking} onChange={v => set('smoking', v)} />
            </Field>
            <Field label={t('field.drinking')} error={errors.drinking}>
              <ChipSelect options={DRINKING_OPTIONS} value={form.drinking} onChange={v => set('drinking', v)} />
            </Field>
            <Field label={t('field.speciallyAbled')}>
              <View style={styles.switchRow}>
                <Switch value={form.speciallyAbled} onValueChange={v => set('speciallyAbled', v)}
                  trackColor={{ false: THEME.border, true: THEME.primary }} thumbColor="#fff" />
                <Text style={styles.switchLabel}>{form.speciallyAbled ? t('yes') : t('no')}</Text>
              </View>
            </Field>
            {form.speciallyAbled && (
              <Field label={t('field.specialAbility')}>
                <TF value={form.specialAbility} onChangeText={v => set('specialAbility', v)} placeholder="Describe (optional)" maxLength={200} />
              </Field>
            )}
            <Field label={t('field.otherContactNo')}>
              <TF value={form.otherContactNo} onChangeText={v => set('otherContactNo', v)} placeholder="Alternate number (optional)" keyboardType="phone-pad" />
            </Field>
            <Field label={t('field.profileDetails')}>
              <TF value={form.profileDetails} onChangeText={v => set('profileDetails', v)} placeholder="Tell something about yourself..." multiline maxLength={1000} numberOfLines={8} />
            </Field>

          </View>
        )}

      </ScrollView>

      {/* Sticky Footer — always visible above keyboard */}
      <View style={[styles.stickyFooter, { paddingBottom: insets.bottom + 8 }]}>
        {step === TOTAL_STEPS ? (
          <View style={styles.submitRow}>
            <TouchableOpacity style={styles.saveDraftBtn} onPress={handleSave}>
              <Text style={styles.saveDraftText}>Save Changes</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.submitBtn}
              onPress={handleSubmit}
              disabled={submitting}>
              {submitting
                ? <ActivityIndicator color="#fff" />
                : <Text style={styles.submitBtnText}>Submit for Review</Text>}
            </TouchableOpacity>
          </View>
        ) : (
          <View style={styles.navRow}>
            {step > 1 ? (
              <TouchableOpacity style={styles.backBtn} onPress={goBack}>
                <Text style={styles.backBtnText}>← Back</Text>
              </TouchableOpacity>
            ) : (
              <View />
            )}
            <TouchableOpacity
              style={[styles.nextBtn, step === 1 && { marginLeft: 'auto' }, nextSaving && { opacity: 0.7 }]}
              onPress={goNext}
              disabled={nextSaving}
              activeOpacity={0.8}>
              {nextSaving
                ? <ActivityIndicator size="small" color="#fff" />
                : <Text style={styles.nextBtnText}>Next →</Text>}
            </TouchableOpacity>
          </View>
        )}
      </View>

    </KeyboardAvoidingView>
    </SafeAreaView>
  );
};

export default EditProfileScreen;

const styles = StyleSheet.create({
  readonlyRow: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    borderWidth: 1.5, borderColor: THEME.border, borderRadius: 12,
    paddingHorizontal: 14, paddingVertical: 13, backgroundColor: '#F5F5F5',
  },
  readonlyText: { fontSize: 15, color: THEME.textLight, flex: 1 },
  genderHint: { fontSize: 12, color: THEME.textLight, marginTop: 6, lineHeight: 17 },
  genderHintLink: { fontSize: 12, color: THEME.primary, textDecorationLine: 'underline' },
  progressHeader: {
    backgroundColor: THEME.card, paddingHorizontal: 20, paddingTop: 16, paddingBottom: 12,
    borderBottomWidth: 1, borderBottomColor: THEME.border,
  },
  progressTopRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 },
  stepTitle: { fontSize: 17, fontWeight: '800', color: THEME.text },
  stepCount: { fontSize: 12, color: THEME.textLight },
  completionRow: { marginBottom: 6 },
  completionLabel: { fontSize: 11, color: THEME.primary, fontWeight: '600' },
  progressBar: { height: 4, backgroundColor: THEME.border, borderRadius: 2, overflow: 'hidden', marginTop: 6 },
  progressFill: { height: 4, backgroundColor: THEME.primary, borderRadius: 2 },
  container: { flex: 1, backgroundColor: THEME.bg, paddingHorizontal: 20, paddingTop: 20 },
  field: { marginBottom: 20 },
  label: { fontSize: 13, fontWeight: '700', color: THEME.textLight, marginBottom: 8 },
  input: {
    borderWidth: 1.5, borderColor: THEME.border, borderRadius: 12,
    paddingHorizontal: 14, paddingVertical: 12, fontSize: 15,
    color: THEME.text, backgroundColor: THEME.card,
  },
  inputError: { borderColor: '#E74C3C', borderWidth: 2 },
  fieldError: { color: '#E74C3C', fontSize: 12, fontWeight: '700', marginTop: 6 },
  charCount: { fontSize: 11, color: THEME.textLight, textAlign: 'right', marginTop: 2 },
  charCountLimit: { color: '#E74C3C', fontWeight: '700' },
  otherInput: { marginTop: 8 },
  genderToggle: {
    flexDirection: 'row', borderRadius: 30, borderWidth: 1.5,
    borderColor: THEME.border, backgroundColor: THEME.card,
    overflow: 'hidden', position: 'relative', height: 46,
  },
  genderToggleError: { borderColor: THEME.danger },
  genderThumb: {
    position: 'absolute', top: 2, bottom: 2, width: '50%',
    borderRadius: 26, backgroundColor: THEME.primary,
  },
  genderThumbLeft: { left: 2 },
  genderThumbRight: { right: 2 },
  genderOption: { flex: 1, alignItems: 'center', justifyContent: 'center', zIndex: 1 },
  genderOptionText: { fontSize: 15, fontWeight: '600', color: THEME.textLight },
  genderOptionTextActive: { color: '#fff' },
  chipRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  chip: { paddingHorizontal: 14, paddingVertical: 8, borderRadius: 20, borderWidth: 1.5, borderColor: THEME.border, backgroundColor: THEME.card },
  chipActive: { backgroundColor: THEME.primary, borderColor: THEME.primary },
  chipText: { fontSize: 13, fontWeight: '600', color: THEME.textLight },
  chipTextActive: { color: '#fff' },
  // Modal dropdown
  dropdownBtn: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    borderWidth: 1.5, borderColor: THEME.border, borderRadius: 12,
    paddingHorizontal: 14, paddingVertical: 13, backgroundColor: THEME.card,
  },
  dropdownBtnActive: { borderColor: THEME.primary },
  dropdownBtnDisabled: { opacity: 0.5 },
  dropdownBtnText: { fontSize: 15, color: THEME.textLight, flex: 1 },
  dropdownBtnTextActive: { color: THEME.primary, fontWeight: '600' },
  modalOverlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.4)', justifyContent: 'flex-end' },
  modalSheet: {
    backgroundColor: THEME.bg, borderTopLeftRadius: 20, borderTopRightRadius: 20,
    height: '75%',
  },
  modalHeader: {
    flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
    paddingHorizontal: 20, paddingTop: 20, paddingBottom: 12,
    borderBottomWidth: 1, borderBottomColor: THEME.border,
  },
  modalTitle: { fontSize: 17, fontWeight: '800', color: THEME.text },
  modalSearch: {
    flexDirection: 'row', alignItems: 'center', gap: 10,
    marginHorizontal: 16, marginVertical: 12,
    borderWidth: 1.5, borderColor: THEME.border, borderRadius: 12,
    paddingHorizontal: 12, paddingVertical: 10, backgroundColor: THEME.card,
  },
  modalSearchInput: { flex: 1, fontSize: 15, color: THEME.text, padding: 0 },
  modalOption: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingHorizontal: 20, paddingVertical: 14,
    borderBottomWidth: 1, borderBottomColor: THEME.border,
  },
  modalOptionActive: { backgroundColor: '#FAE5E5' },
  modalOptionText: { fontSize: 15, color: THEME.text },
  modalOptionTextActive: { color: THEME.primary, fontWeight: '700' },
  modalEmpty: { alignItems: 'center', justifyContent: 'center', paddingVertical: 48, gap: 12 },
  modalEmptyText: { fontSize: 14, color: THEME.textLight, textAlign: 'center' },
  modalOptionAlt: { fontSize: 13, color: THEME.textLight, marginTop: 1 },
  modalOptionAltActive: { color: THEME.primary },
  switchRow: { flexDirection: 'row', alignItems: 'center', gap: 12 },
  switchLabel: { fontSize: 15, color: THEME.text, fontWeight: '600' },
  stickyFooter: {
    backgroundColor: THEME.card,
    paddingHorizontal: 16, paddingTop: 12,
    borderTopWidth: 1, borderTopColor: THEME.border,
  },
  navRow: { flexDirection: 'row', justifyContent: 'space-between', gap: 12 },
  backBtn: { paddingVertical: 14, paddingHorizontal: 24, borderRadius: 12, borderWidth: 1.5, borderColor: THEME.border, backgroundColor: THEME.card },
  backBtnText: { fontSize: 15, fontWeight: '700', color: THEME.text },
  nextBtn: { flex: 1, paddingVertical: 14, borderRadius: 12, backgroundColor: THEME.primary, alignItems: 'center' },
  nextBtnText: { fontSize: 15, fontWeight: '700', color: '#fff' },
  submitRow: { flexDirection: 'row', gap: 12 },
  saveDraftBtn: { paddingVertical: 14, paddingHorizontal: 20, borderRadius: 12, borderWidth: 1.5, borderColor: THEME.border, backgroundColor: THEME.card },
  saveDraftText: { fontSize: 14, fontWeight: '700', color: THEME.text },
  submitBtn: { flex: 1, paddingVertical: 14, borderRadius: 12, backgroundColor: THEME.primary, alignItems: 'center' },
  submitBtnText: { fontSize: 15, fontWeight: '800', color: '#fff' },
  socialInputRow: {
    flexDirection: 'row', alignItems: 'center',
    borderWidth: 1.5, borderColor: THEME.border, borderRadius: 12,
    overflow: 'hidden', backgroundColor: THEME.card,
  },
  socialPrefix: {
    fontSize: 13, color: THEME.textLight, paddingHorizontal: 10, paddingVertical: 12,
    backgroundColor: '#F0F0F0', borderRightWidth: 1, borderRightColor: THEME.border,
  },
  socialInput: {
    flex: 1, fontSize: 15, color: THEME.text,
    paddingHorizontal: 12, paddingVertical: 12,
  },
  fieldHint: { fontSize: 11, color: THEME.textLight, marginTop: 4 },
});
